import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ShoppingBag, Phone, MessageCircle } from "lucide-react";
import { BRAND, wa } from "@/lib/brand";
import { useEnquiry } from "@/context/EnquiryContext";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products" },
  { to: "/quality", label: "Quality" },
  { to: "/packaging", label: "Packaging" },
  { to: "/logistics", label: "Logistics" },
  { to: "/global-supply", label: "Global Supply" },
  { to: "/certifications", label: "Certifications" },
  { to: "/contact", label: "Contact" },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { count } = useEnquiry();
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled ? "bg-white/92 backdrop-blur-xl border-b border-[#004225]/10 shadow-sm" : "bg-white/85 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 py-3">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <img src={BRAND.logo} alt="GN Agro Traders" className={`transition-all ${scrolled ? "h-11" : "h-14"} w-auto`} />
          <div className="hidden sm:block leading-tight">
            <div className="font-display text-lg text-[#004225]">GN Agro Traders</div>
            <div className="text-[10px] font-eyebrow text-[#B5942D]">Pure Grains · Global Standards</div>
          </div>
        </Link>

        <nav className="hidden lg:flex items-center gap-6">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}-link`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors hover:text-[#004225] ${
                  isActive ? "text-[#004225]" : "text-[#171717]/75"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          <a
            href={`tel:${BRAND.phoneTel}`}
            data-testid="header-phone-btn"
            className="hidden 2xl:inline-flex items-center gap-1.5 text-xs font-semibold text-[#004225] hover:text-[#B5942D] transition-colors whitespace-nowrap"
          >
            <Phone className="h-3.5 w-3.5" />{BRAND.phone}
          </a>
          <Link
            to="/enquiry-cart"
            data-testid="enquiry-cart-btn"
            className="relative inline-flex items-center gap-2 rounded-full border border-[#004225]/15 px-3 py-2 text-xs font-medium text-[#004225] hover:bg-[#F7F2E2]"
          >
            <ShoppingBag className="h-4 w-4" />
            <span className="hidden sm:inline">Enquiry</span>
            {count > 0 && (
              <span
                data-testid="enquiry-count"
                className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D4AF37] text-[10px] font-bold text-[#004225] flex items-center justify-center"
              >
                {count}
              </span>
            )}
          </Link>

          <Link
            to="/request-quote"
            data-testid="header-quote-btn"
            className="hidden sm:inline-flex items-center gap-2 rounded-full btn-primary px-4 py-2 text-xs font-semibold uppercase tracking-wider transition-colors"
          >
            Request Quote
          </Link>

          <a
            href={wa()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="header-whatsapp-btn"
            className="inline-flex items-center justify-center h-9 w-9 rounded-full bg-[#25D366] text-white hover:opacity-90"
            aria-label="WhatsApp"
          >
            <MessageCircle className="h-4 w-4" />
          </a>

          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-btn"
            className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-full border border-[#004225]/15 text-[#004225]"
            aria-label="Menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white border-t border-[#004225]/10" data-testid="mobile-menu">
          <div className="px-4 py-3 flex flex-col gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                end={n.to === "/"}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm ${isActive ? "bg-[#F7F2E2] text-[#004225]" : "text-[#171717]/80 hover:bg-[#F4F4ED]"}`
                }
              >
                {n.label}
              </NavLink>
            ))}
            <div className="mt-2 flex gap-2">
              <Link to="/request-quote" className="flex-1 text-center rounded-full btn-primary px-4 py-2 text-xs font-semibold uppercase tracking-wider">Request Quote</Link>
              <a href={`tel:${BRAND.phoneTel}`} className="rounded-full bg-[#F7F2E2] text-[#004225] px-4 py-2 text-xs font-semibold flex items-center gap-1"><Phone className="h-3.5 w-3.5" />Call</a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
